import { EditorType, Language } from "@kaggle/sdk/kernels";
import NotebookContent from "../connector/extensions/types/ipynb/NotebookContent";
import NotebookMetadata from "../connector/extensions/types/ipynb/NotebookMetadata";
import {
  convertCodeToIpynb,
  convertToScript,
} from "../utils/convertEditorFormats";

/** Serialize NotebookContent to string. */
const serializeNotebookContent = (content: NotebookContent) => {
  return JSON.stringify(content);
};

export const DEFAULT_PYTHON_METADATA: NotebookMetadata = {
  kernelspec: {
    language: "python",
    display_name: "Python 3",
    name: "python3",
  },
  language_info: {
    pygments_lexer: "ipython3",
    nbconvert_exporter: "python",
    version: "3.6.4",
    file_extension: ".py",
    codemirror_mode: {
      name: "ipython",
      version: 3,
    },
    name: "python",
    mimetype: "text/x-python",
  },
};

export const DEFAULT_R_METADATA: NotebookMetadata = {
  kernelspec: {
    display_name: "R",
    language: "R",
    name: "ir",
  },
  language_info: {
    mimetype: "text/x-r-source",
    name: "R",
    pygments_lexer: "r",
    version: "3.4.2",
    file_extension: ".r",
    codemirror_mode: "r",
  },
};

// Please discuss any version change with the backend team (#kernels-backend)
// as it impacts nbconvert.
export const NB_FORMATS = {
  nbformat: 4,
  nbformat_minor: 4,
};

/** Array of string fields that should be stripped out of cell metadata. */
const CELL_METADATA_FIELD_STOP_LIST = [
  // Caches output cells on Colab. Causes ipynb size to be very large (base64'ed images easily exceed 1 MiB).
  "colab",
  // Caches the type of input cell in Colab but has no bearing on our system. Removing to trim size.
  "colab_type",
];

const parseJSON = (ipynb: string): NotebookContent => {
  const jsonContent = JSON.parse(ipynb);

  // We expect nbformat.v4: https://github.com/jupyter/nbformat/blob/master/nbformat/v4/nbformat.v4.schema.json
  // In this format, there is a "cells" array field at the root.

  // Apply conversion to support nbformat.v3: https://github.com/jupyter/nbformat/blob/master/nbformat/v3/nbformat.v3.schema.json
  // The nbformat.v3 has a "worksheets" array field. Each "worksheet" includes a "cells" array field.
  if (jsonContent.worksheets && !jsonContent.cells) {
    jsonContent.cells = [];
    for (const worksheet of jsonContent.worksheets) {
      if (worksheet.cells) {
        for (const cell of worksheet.cells) {
          if (cell.cell_type === "code") {
            // The `source` field in v3 is named `input`.
            cell.source = cell.input;
          }
          if (cell.cell_type === "heading") {
            // The `heading` cell_type doesn't exist in v4.
            // Convert to `markdown` cell with appropriate header `level`.
            cell.cell_type = "markdown";
            cell.source = convertHeadingSource(cell);
          }
          jsonContent.cells.push(cell);
        }
      }
    }
  }

  return jsonContent as NotebookContent;
};

const convertHeadingSource = (cell: any) => {
  let level = 1;
  if (cell.level) {
    level = cell.level;
  }

  // level 1: # foo
  // level 2: ## foo
  // ...
  let headingPrefix = "";
  for (let i = 0; i < level; i++) {
    headingPrefix += "#";
  }

  if (typeof cell.source === "string") {
    return `${headingPrefix} ${cell.source}`;
  }
  const source = [];
  for (const line of cell.source) {
    source.push(`${headingPrefix} ${line}`);
  }
  return source;
};

/** Normalizes notebook content. */
const normalizeContent = (content: NotebookContent): NotebookContent => ({
  ...content,
  ...NB_FORMATS,
  ...(!content.metadata?.kernelspec?.name ||
  content.metadata.kernelspec.name.match(/python/i)
    ? { metadata: DEFAULT_PYTHON_METADATA }
    : { metadata: content.metadata }),
  cells: content.cells.map((cell: any) => {
    // It may be possible to handle this mid-tier only. This is the logic used historically.
    // Source can be a string or list of strings (when characters need to be escaped). Join them.
    if (cell.source instanceof Array) {
      cell.source = cell.source.join("");
    }

    // Strip out any metadata keys that are problematic.
    if (cell.metadata) {
      const metadataKeys = Object.keys(cell.metadata);
      cell.metadata = metadataKeys.reduce(
        (result, key: keyof NotebookMetadata) => {
          // Only carry forward the value if its not in stop list.
          if (!CELL_METADATA_FIELD_STOP_LIST.includes(key)) {
            result[key] = cell.metadata[key];
          }
          return result;
        },
        {} as any
      );
    }

    // Strip notebook output when persisting to keep ipynb under 1 MiB in databases.
    if (cell.outputs) {
      cell.outputs = cell.cell_type === "markdown" ? undefined : [];
    }

    if (cell.cell_type === "code") {
      cell.execution_count = null;
    }

    return cell;
  }),
});

/**
 * Parses a serialized ipynb into NotebookContent interface (object form).
 *
 * This method may throw during validation of the object. Callers must handle exception.
 */
const deserializeNotebookContent = (ipynb: string): NotebookContent => {
  return normalizeContent(parseJSON(ipynb));
};

/**
 * Converts the content of a file to a target jupyter source type (notebook or script) and language.
 */
export const convertFileToJupyterSource = (
  raw: string,
  sourceType: EditorType,
  languageName: Language
): JupyterSource => {
  let source: JupyterSource;
  try {
    source = new JupyterSource(raw);
    source.validate();
  } catch {
    // Fallback: assume the uploaded file contains raw source code - convert it to ipynb JSON format.
    source = JupyterSource.fromNotebookContent(
      convertCodeToIpynb(raw, sourceType, languageName)
    );
  }

  // If script, detect if ipynb has multiple cells and if so, translate it to script encoded single-cell.
  if (sourceType === EditorType.SCRIPT) {
    const content = source.toNotebookContent();
    if (content.cells?.length > 1) {
      const scriptContent = convertToScript(content);
      source = JupyterSource.fromNotebookContent(scriptContent);
    }
  }

  return source;
};

/**
 * Wrapper that holds the ipynb string source of a Jupyter notebook.
 *
 * Most cases should just pass in an ipynb string. For performance purposes,
 * content can be optionally passed if it was already parsed (only recommended internally).
 */
export class JupyterSource {
  constructor(
    private readonly ipynb: string,
    private content?: NotebookContent
  ) {}

  /** Eagerly transform NotebookContent object to ipynb source. */
  static fromNotebookContent(content: NotebookContent): JupyterSource {
    // Since we already have the content, we hydrate with it.
    // This ensures that the source won't be reparsed later and improves immutability.
    return new JupyterSource(serializeNotebookContent(content), content);
  }

  /** Get the ipynb string form. */
  serialize(): string {
    return this.ipynb;
  }

  /** Strip output cells, etc. and serialize back to ipynb string form. */
  stripAndSerialize(): string {
    return serializeNotebookContent(normalizeContent(this.toNotebookContent()));
  }

  /** Convert source to NotebookContent (object form). */
  toNotebookContent(): NotebookContent {
    if (this.content === undefined) {
      this.content = deserializeNotebookContent(this.ipynb);
    }
    return this.content;
  }

  getCellSizes(): object[] {
    try {
      return JSON.parse(this.ipynb).cells.map((cell: any) => {
        return {
          cellId: cell?.metadata?._cell_guid || "",
          sourceSize: JSON.stringify(cell?.source)?.length || 0,
          outputSize: JSON.stringify(cell?.outputs)?.length || 0,
        };
      });
    } catch (e) {
      return [];
    }
  }

  /** Validate that the ipynb source contains valid content. */
  validate(): void {
    // If invalid, this will throw.
    this.toNotebookContent();
  }

  /** Override toString (useful for console logging, etc.) */
  toString() {
    return this.serialize();
  }
}
